import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Building2,
  ExternalLink,
  Pause,
  Play,
  Trash2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  Database
} from 'lucide-react';
import toast from 'react-hot-toast';

const statusColors = {
  active: 'badge-success',
  trialing: 'badge-info',
  past_due: 'badge-warning',
  canceled: 'badge-danger',
  suspended: 'badge-danger',
  pending: 'badge-gray'
};

const TenantsPage = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncForm, setSyncForm] = useState({ subdomain: '', firmName: '' });
  const [syncing, setSyncing] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    subscriptionStatus: searchParams.get('subscriptionStatus') || ''
  });

  useEffect(() => {
    fetchTenants();
  }, [searchParams]);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const params = {
        page: searchParams.get('page') || 1,
        search: searchParams.get('search') || '',
        status: searchParams.get('status') || '',
        subscriptionStatus: searchParams.get('subscriptionStatus') || '',
        limit: 20
      };

      const response = await adminApi.getTenants(params);
      setTenants(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      toast.error('Failed to load tenants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (filters.search) {
      newParams.set('search', filters.search);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPage));
    setSearchParams(newParams);
  };

  const handleSuspend = async (tenant) => {
    if (!confirm(`Are you sure you want to suspend ${tenant.name}?`)) return;

    try {
      await adminApi.suspendTenant(tenant.id, 'Suspended by admin');
      toast.success('Tenant suspended');
      fetchTenants();
    } catch (err) {
      toast.error('Failed to suspend tenant');
    }
  };

  const handleActivate = async (tenant) => {
    try {
      await adminApi.activateTenant(tenant.id);
      toast.success('Tenant activated');
      fetchTenants();
    } catch (err) {
      toast.error('Failed to activate tenant');
    }
  };

  const handleGetLoginLink = async (tenant) => {
    try {
      const response = await adminApi.getTenantLoginLink(tenant.id);
      navigator.clipboard.writeText(response.data.loginUrl);
      toast.success('Login link copied to clipboard');
    } catch (err) {
      toast.error('Failed to generate login link');
    }
  };

  const handleSyncFromNeon = async (e) => {
    e.preventDefault();
    if (!syncForm.subdomain.trim()) {
      toast.error('Subdomain is required');
      return;
    }

    setSyncing(true);
    try {
      const response = await adminApi.syncTenantFromNeon({
        subdomain: syncForm.subdomain.trim().toLowerCase(),
        firmName: syncForm.firmName.trim() || undefined
      });
      toast.success(response.data.message || 'Tenant synced successfully');
      setShowSyncModal(false);
      setSyncForm({ subdomain: '', firmName: '' });
      fetchTenants();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to sync tenant from Neon';
      toast.error(errorMsg);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
          <p className="text-gray-500 mt-1">Manage all customer accounts</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSyncModal(true)}
            className="btn-secondary"
          >
            <Database className="w-5 h-5 mr-2" />
            Sync from Neon
          </button>
          <Link to="/tenants/new" className="btn-primary">
            <Plus className="w-5 h-5 mr-2" />
            New Tenant
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search tenants..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                className="input pl-10"
              />
            </div>
          </form>

          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>

            <select
              value={filters.subscriptionStatus}
              onChange={(e) => handleFilterChange('subscriptionStatus', e.target.value)}
              className="input"
            >
              <option value="">All Subscriptions</option>
              <option value="trialing">Trialing</option>
              <option value="active">Active</option>
              <option value="past_due">Past Due</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-header">Tenant</th>
                <th className="table-header">Plan</th>
                <th className="table-header">Status</th>
                <th className="table-header">Users</th>
                <th className="table-header">Created</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-8">
                    <div className="inline-flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mr-2"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : tenants.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    No tenants found
                  </td>
                </tr>
              ) : (
                tenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <Link
                        to={`/tenants/${tenant.slug}`}
                        className="flex items-center group"
                      >
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                          <Building2 className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 group-hover:text-primary-600">
                            {tenant.name}
                          </p>
                          <p className="text-xs text-gray-500">{tenant.slug}</p>
                        </div>
                      </Link>
                    </td>
                    <td className="table-cell">
                      <span className="text-gray-900">
                        {tenant.subscriptionPlan?.name || 'No plan'}
                      </span>
                      <span className="text-xs text-gray-500 block">
                        {tenant.billingCycle === 'yearly' ? 'Annual' : 'Monthly'}
                      </span>
                    </td>
                    <td className="table-cell">
                      <span className={statusColors[tenant.subscriptionStatus] || 'badge-gray'}>
                        {tenant.subscriptionStatus}
                      </span>
                      {tenant.status === 'suspended' && (
                        <span className="badge-danger ml-1">Suspended</span>
                      )}
                    </td>
                    <td className="table-cell text-gray-500">
                      {tenant.userCount || tenant.currentUsersCount || 0}
                    </td>
                    <td className="table-cell text-gray-500">
                      {new Date(tenant.createdAt).toLocaleDateString()}
                    </td>
                    <td className="table-cell text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleGetLoginLink(tenant)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                          title="Copy login link"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        {tenant.status === 'suspended' ? (
                          <button
                            onClick={() => handleActivate(tenant)}
                            className="p-2 text-green-600 hover:text-green-700 rounded-lg hover:bg-green-50"
                            title="Activate"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleSuspend(tenant)}
                            className="p-2 text-orange-600 hover:text-orange-700 rounded-lg hover:bg-orange-50"
                            title="Suspend"
                          >
                            <Pause className="w-4 h-4" />
                          </button>
                        )}
                        <Link
                          to={`/tenants/${tenant.slug}`}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 20 + 1} to{' '}
              {Math.min(pagination.page * 20, pagination.total)} of {pagination.total} tenants
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 text-sm text-gray-700">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sync from Neon Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowSyncModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Database className="w-5 h-5 text-primary-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Sync from Neon</h2>
                </div>
                <button
                  onClick={() => setShowSyncModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                Import an existing tenant from a Neon project. The project must be named <code className="bg-gray-100 px-1 rounded">pmp-subdomain</code>.
              </p>

              <form onSubmit={handleSyncFromNeon} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subdomain <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={syncForm.subdomain}
                    onChange={(e) => setSyncForm(prev => ({ ...prev, subdomain: e.target.value }))}
                    placeholder="e.g., acmeaccounting"
                    className="input"
                    autoFocus
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Must match the Neon project name (without "pmp-" prefix)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Firm Name <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={syncForm.firmName}
                    onChange={(e) => setSyncForm(prev => ({ ...prev, firmName: e.target.value }))}
                    placeholder="e.g., ACME Accounting Pty Ltd"
                    className="input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowSyncModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={syncing || !syncForm.subdomain.trim()}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {syncing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Database className="w-4 h-4 mr-2" />
                        Sync Tenant
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantsPage;
